import { z } from "zod";

const MAX_ARCHIVE_FILENAME_CODE_POINTS = 180;
const WINDOWS_RESERVED_BASENAME =
  /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i;

const trelloIdSchema = z
  .string()
  .regex(/^[a-f0-9]{24}$/, "Expected a 24-character Trello ID.");

export const attachmentSourceTypeSchema = z.enum([
  "trello_upload",
  "direct_https",
  "google_drive",
  "dropbox",
  "onedrive",
  "unsupported",
]);
export type AttachmentSourceType = z.infer<typeof attachmentSourceTypeSchema>;

export const attachmentDispositionSchema = z.enum([
  "copy_candidate",
  "link_only",
  "skip",
]);
export type AttachmentDisposition = z.infer<typeof attachmentDispositionSchema>;

export const attachmentClassificationWarningSchema = z.enum([
  "ATTACHMENT_URL_MISSING",
  "ATTACHMENT_URL_INVALID",
  "ATTACHMENT_SCHEME_UNSUPPORTED",
  "ATTACHMENT_URL_CREDENTIALS",
]);
export type AttachmentClassificationWarning = z.infer<
  typeof attachmentClassificationWarningSchema
>;

export const attachmentSourceInputSchema = z
  .object({
    isUpload: z.boolean(),
    url: z.string().min(1).nullable(),
  })
  .strict();
export type AttachmentSourceInput = z.infer<typeof attachmentSourceInputSchema>;

export const attachmentClassificationSchema = z
  .object({
    disposition: attachmentDispositionSchema,
    sourceType: attachmentSourceTypeSchema,
    warningCode: attachmentClassificationWarningSchema.nullable(),
  })
  .strict();
export type AttachmentClassification = z.infer<
  typeof attachmentClassificationSchema
>;

const attachmentFilenameInputSchema = z
  .object({
    attachmentId: trelloIdSchema,
    name: z.string(),
  })
  .strict();
export type AttachmentFilenameInput = z.infer<
  typeof attachmentFilenameInputSchema
>;

function hostnameMatches(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function classifyProvider(hostname: string): AttachmentSourceType | undefined {
  if (
    hostnameMatches(hostname, "drive.google.com") ||
    hostnameMatches(hostname, "docs.google.com")
  ) {
    return "google_drive";
  }

  if (
    hostnameMatches(hostname, "dropbox.com") ||
    hostnameMatches(hostname, "dropboxusercontent.com")
  ) {
    return "dropbox";
  }

  if (
    hostnameMatches(hostname, "1drv.ms") ||
    hostnameMatches(hostname, "onedrive.live.com") ||
    hostnameMatches(hostname, "sharepoint.com")
  ) {
    return "onedrive";
  }

  return undefined;
}

function skipped(
  warningCode: AttachmentClassificationWarning,
): AttachmentClassification {
  return {
    disposition: "skip",
    sourceType: "unsupported",
    warningCode,
  };
}

export function classifyAttachmentSource(
  input: AttachmentSourceInput,
): AttachmentClassification {
  const parsed = attachmentSourceInputSchema.parse(input);

  if (parsed.url === null) {
    return skipped("ATTACHMENT_URL_MISSING");
  }

  let url: URL;
  try {
    url = new URL(parsed.url);
  } catch {
    return skipped("ATTACHMENT_URL_INVALID");
  }

  if (url.protocol !== "https:") {
    return skipped("ATTACHMENT_SCHEME_UNSUPPORTED");
  }

  if (url.username !== "" || url.password !== "") {
    return skipped("ATTACHMENT_URL_CREDENTIALS");
  }

  if (parsed.isUpload) {
    return {
      disposition: "copy_candidate",
      sourceType: "trello_upload",
      warningCode: null,
    };
  }

  const provider = classifyProvider(url.hostname.toLowerCase());
  if (provider !== undefined) {
    return {
      disposition: "link_only",
      sourceType: provider,
      warningCode: null,
    };
  }

  return {
    disposition: "copy_candidate",
    sourceType: "direct_https",
    warningCode: null,
  };
}

function truncateCodePoints(value: string, maximum: number): string {
  return Array.from(value).slice(0, maximum).join("");
}

function removeControlCharacters(value: string): string {
  return Array.from(value)
    .filter((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint > 31 && codePoint !== 127;
    })
    .join("");
}

export function normalizeAttachmentFilename(name: string): string {
  const normalized = removeControlCharacters(name.normalize("NFC"))
    .replace(/[<>:"/\\|?*]/g, "_")
    .trim()
    .replace(/[. ]+$/g, "");

  let safeName =
    normalized === "" || normalized === "." || normalized === ".."
      ? "attachment"
      : normalized;

  if (WINDOWS_RESERVED_BASENAME.test(safeName)) {
    safeName = `_${safeName}`;
  }

  return truncateCodePoints(safeName, MAX_ARCHIVE_FILENAME_CODE_POINTS);
}

export function createAttachmentArchiveFilenames(
  inputs: readonly AttachmentFilenameInput[],
): ReadonlyMap<string, string> {
  const parsedInputs = inputs.map((input) =>
    attachmentFilenameInputSchema.parse(input),
  );
  const ids = parsedInputs.map(({ attachmentId }) => attachmentId);

  if (new Set(ids).size !== ids.length) {
    throw new RangeError("Attachment IDs must be unique.");
  }

  const normalized = parsedInputs.map((input) => ({
    ...input,
    safeName: normalizeAttachmentFilename(input.name),
  }));
  const collisionCounts = new Map<string, number>();

  for (const { safeName } of normalized) {
    const collisionKey = safeName.toLocaleLowerCase("en");
    collisionCounts.set(
      collisionKey,
      (collisionCounts.get(collisionKey) ?? 0) + 1,
    );
  }

  return new Map(
    normalized.map(({ attachmentId, safeName }) => {
      const collisionKey = safeName.toLocaleLowerCase("en");
      const hasCollision = (collisionCounts.get(collisionKey) ?? 0) > 1;
      const archiveName = hasCollision
        ? `${attachmentId}-${safeName}`
        : safeName;

      return [
        attachmentId,
        truncateCodePoints(archiveName, MAX_ARCHIVE_FILENAME_CODE_POINTS),
      ];
    }),
  );
}
